import React, { useState } from 'react';

import { UiInput } from 'ui-kit';

import GridTable from './GridTable';
import {
  useValuationCalcs,
  makeInitial,
  fmtMoney0,
  n,
  type Form,
  type Num,
  type Row,
} from './useValuationCalcs';

type Column = { key: string; label: string; align?: 'left' | 'right' };

const vacancyColumns: Column[] = [
  { key: 'vacancy', label: 'VACANCY RATE' },
  { key: 'noi', label: 'NOI', align: 'right' },
  { key: 'ltv', label: 'LTV', align: 'right' },
];

const expensesColumns: Column[] = [
  { key: 'pct', label: 'EXPENSES SUB TOTAL PERCENTAGE' },
  { key: 'noi', label: 'NOI', align: 'right' },
  { key: 'ltv', label: 'LTV', align: 'right' },
];

const capRateColumns: Column[] = [
  { key: 'cap', label: 'CAP RATE' },
  { key: '_spacer', label: '' },
  { key: 'ltv', label: 'LTV', align: 'right' },
];

type Props = {
  defaultLoanAmount: number;
  defaultPaymentsPerYear: number;
};

export const ValuationCalculator: React.FC<Props> = ({
  defaultLoanAmount = 1,
  defaultPaymentsPerYear = 12,
}) => {
  const [form, setForm] = useState<Form>(() =>
    makeInitial(defaultLoanAmount, defaultPaymentsPerYear),
  );
  const onChange = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  const {
    debtServiceObligation,
    grossIncome,
    vacancyValue,
    egi,
    egiRightLabel,

    operatingExpensesValue,
    repairsValue,
    mgmtFeeValue,
    capexReserveValue,

    expensePctTotal,
    expenseValueTotal,

    noi,
    dscr,

    appraisedValue,
    ltv,

    capRateRows,
    vacancyRows,
    expensesRows,
  } = useValuationCalcs(form);

  return (
    <div className="grid grid-cols-1 gap-6 py-4 lg:grid-cols-12">
      <div className="flex flex-col gap-3 lg:col-span-8">
        <Field label="Loan Amount">
          <UiInput
            size="small"
            variant="light"
            type="number"
            value={form.loanAmount}
            onChange={onChange('loanAmount')}
            prefix="$"
          />
        </Field>

        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <Field label="Amortization (months) *">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.amortizationMonths}
              onChange={onChange('amortizationMonths')}
            />
          </Field>
          <Field label="Interest Rate *">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.interestRate}
              onChange={onChange('interestRate')}
              suffix="%"
              textAlign="right"
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <Field label="Base Rental Income">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.baseRentalIncome}
              onChange={onChange('baseRentalIncome')}
              prefix="$"
            />
          </Field>
          <Field label="Recoverables">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.recoverables}
              onChange={onChange('recoverables')}
              prefix="$"
            />
          </Field>
        </div>

        <Field label="Other">
          <UiInput
            size="small"
            variant="light"
            type="number"
            value={form.otherIncome}
            onChange={onChange('otherIncome')}
            prefix="$"
          />
        </Field>

        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <Field label="Payments/Year">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.paymentsPerYear}
              onChange={onChange('paymentsPerYear')}
              disabled
              disabledVariant="light"
            />
          </Field>
          <Field label="Debt Service Obligation">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={debtServiceObligation}
              onChange={onChange('debtServiceObligation')}
              prefix="$"
              disabled
              disabledVariant="light"
            />
          </Field>
        </div>

        {/* Gross Income + Vacancy */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2">
            <Field label="Gross Income">
              <UiInput
                size="small"
                variant="light"
                type="number"
                value={grossIncome}
                onChange={onChange('grossIncome')}
                prefix="$"
              />
            </Field>
          </div>
          <div className="md:basis-1/2">
            <div className="grid grid-cols-2 items-end gap-2">
              <Field label="Vacancy Rate">
                <UiInput
                  size="small"
                  variant="light"
                  type="number"
                  value={form.vacancyRatePct}
                  onChange={onChange('vacancyRatePct')}
                  suffix="%"
                  textAlign="right"
                />
              </Field>
              <UiInput
                size="small"
                variant="light"
                type="number"
                value={vacancyValue}
                onChange={onChange('vacancyRateValue')}
                prefix="$"
                disabled
                disabledVariant="light"
              />
            </div>
          </div>
        </div>

        {/* EGI */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2" />
          <div className="grid grid-cols-[1fr_auto] items-end gap-3 md:basis-1/2">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={egi}
              onChange={onChange('effectiveGrossIncome')}
              prefix="$"
              className="w-full"
              disabledVariant="light"
              disabled
            />
            <RightLabel label="Effective Gross Income" value={egiRightLabel} />
          </div>
        </div>

        {/* Operating Expenses */}
        <Field label="Operating Expenses">
          <div className="grid grid-cols-2 gap-2">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.operatingExpensesPct}
              onChange={onChange('operatingExpensesPct')}
              suffix="%"
              textAlign="right"
            />
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={operatingExpensesValue}
              onChange={onChange('operatingExpensesValue')}
              prefix="$"
              disabled
              disabledVariant="light"
            />
          </div>
        </Field>

        {/* Repairs */}
        <Field label="Repairs & Maintenance">
          <div className="grid grid-cols-2 gap-2">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.repairsPct}
              onChange={onChange('repairsPct')}
              suffix="%"
              textAlign="right"
            />
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={repairsValue}
              onChange={onChange('repairsValue')}
              prefix="$"
              disabled
              disabledVariant="light"
            />
          </div>
        </Field>

        {/* Mgmt */}
        <Field label="Management Fee">
          <div className="grid grid-cols-2 gap-2">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.mgmtFeePct}
              onChange={onChange('mgmtFeePct')}
              suffix="%"
              textAlign="right"
            />
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={mgmtFeeValue}
              onChange={onChange('mgmtFeeValue')}
              prefix="$"
              disabled
              disabledVariant="light"
            />
          </div>
        </Field>

        {/* Capex */}
        <Field label="Capex Reserve Rate">
          <div className="grid grid-cols-2 gap-2">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={form.capexReservePct}
              onChange={onChange('capexReservePct')}
              suffix="%"
              textAlign="right"
            />
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={capexReserveValue}
              onChange={onChange('capexReserveValue')}
              prefix="$"
              disabled
              disabledVariant="light"
            />
          </div>
        </Field>

        {/* Expense Sub Total */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2" />
          <div className="grid grid-cols-[1fr_auto] items-end gap-3 md:basis-1/2">
            <UiInput
              size="small"
              variant="light"
              type="number"
              value={expensePctTotal}
              onChange={onChange('expenseSubTotal')}
              prefix="$"
              className="w-full"
              disabled
              disabledVariant="light"
            />
            <RightLabel label="Expense Sub Total" value={`$${fmtMoney0(n(expenseValueTotal))}`} />
          </div>
        </div>

        {/* NOI */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2" />
          <div className="md:basis-1/2">
            <Field label="Net Operating Income (NOI)">
              <UiInput
                size="small"
                variant="light"
                type="number"
                value={noi}
                onChange={onChange('noi')}
                prefix="$"
                className="w-full"
                disabled
                disabledVariant="light"
              />
            </Field>
          </div>
        </div>

        {/* DSO */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2" />
          <div className="md:basis-1/2">
            <Field label="Debt Service Obligation">
              <UiInput
                size="small"
                variant="light"
                type="number"
                value={debtServiceObligation}
                onChange={onChange('debtServiceObligation')}
                prefix="$"
                className="w-full"
                disabled
                disabledVariant="light"
              />
            </Field>
          </div>
        </div>

        {/* DSCR */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2" />
          <div className="md:basis-1/2">
            <Field label="DSCR">
              <UiInput
                size="small"
                variant="light"
                type="number"
                value={dscr}
                onChange={onChange('dscr')}
                className="w-full"
                disabled
                disabledVariant="light"
              />
            </Field>
          </div>
        </div>

        {/* Cap Rate */}
        <Field label="Cap Rate">
          <UiInput
            size="small"
            variant="light"
            type="number"
            value={form.capRate}
            onChange={onChange('capRate')}
            suffix="%"
            textAlign="right"
          />
        </Field>

        {/* LTV + Appraised Value */}
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="md:basis-1/2" />
          <div className="grid grid-cols-[1fr_auto] items-start gap-3 md:basis-1/2">
            <Field label="LTV">
              <UiInput
                size="small"
                variant="light"
                type="number"
                value={ltv}
                onChange={onChange('ltv')}
                className="w-full"
                disabled
                disabledVariant="light"
              />
            </Field>
            <RightLabel label="Appraised Value" value={`$${fmtMoney0(n(appraisedValue))}`} />
          </div>
        </div>
      </div>

      <aside className="space-y-6 lg:col-span-4">
        <div className="bg-gray-950 px-8 py-4">
          <div className="space-y-8">
            <GridTable columns={vacancyColumns} rows={vacancyRows as Row[]} />
            <GridTable columns={expensesColumns} rows={expensesRows as Row[]} />
            <GridTable columns={capRateColumns} rows={capRateRows as Row[]} />
          </div>
        </div>
      </aside>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex w-full flex-col gap-1 md:flex-1">
    <span className="text-[12px] text-slate-300">{label}</span>
    {children}
  </label>
);

const RightLabel = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col items-end">
    <span className="mb-1 text-xs text-slate-300">{label}</span>
    <span className="text-lg font-bold text-white">{value}</span>
  </div>
);

export default ValuationCalculator;
